import { Box3 } from 'three';
import type { BufferGeometry, Group, Mesh, MeshStandardMaterial } from 'three';

import type { CutoutManager } from '../cutout-manager.class';
import { loadCSG } from '../../../../utils/lazy-three-csg';
import { applySurroundMaterials, classifySurroundFaces, cloneMaterial, cloneMaterialArray, addColorAttribute, disposeUnusedSurroundMaterials, resetMaterialOpacity } from '../helpers';
import { createCutoutMeshCfn } from './create-cutout-mesh.cfn';

export interface GenerateCutoutsParams {
	islands: Group[];
	onCompletion?: () => void;
}

/**
 * After CSG operations the countertop geometry groups can end up with
 * material indices beyond the valid range (0-1). This clamps every group's
 * materialIndex to 0 (caps / cutout walls) or 1 (outer side edges) so the
 * two-material array [cap, edge] renders correctly.
 *
 * Ported from Haxe ThreeComponent.fixCounterMaterials().
 */
const fixCounterMaterials = (mesh: Mesh): void => {
	const material = mesh.material;
	// Only applies to multi-material countertops
	if (!Array.isArray(material)) return;

	const { groups } = mesh.geometry as BufferGeometry;
	if (!groups || groups.length === 0) return;

	for (const group of groups) {
		// Clamp to valid range: 0 stays 0, anything else becomes 1
		group.materialIndex = group.materialIndex === 0 ? 0 : 1;
	}
};

/**
 * Clone a mesh for cutout toggle cross-fade.
 * Clones geometry AND materials so the clone is fully independent.
 * Geometry must be cloned because the source mesh may be disposed during
 * structural cleanup, which would invalidate shared geometry references.
 */
const cloneForCutoutToggle = (source: Mesh, type: string, id: string): Mesh => {
	const clone = source.clone() as Mesh;
	clone.geometry = clone.geometry.clone();
	if (Array.isArray(clone.material)) {
		clone.material = clone.material.map((m) => {
			const cloned = m.clone();
			resetMaterialOpacity(cloned as MeshStandardMaterial);
			return cloned;
		});
	} else {
		clone.material = clone.material.clone();
		resetMaterialOpacity(clone.material as MeshStandardMaterial);
	}
	clone.userData = { type, id };
	clone.visible = false;
	return clone;
};

/**
 * Dress a merged surround mesh. The cap split survives CSG, so prefer it; a mesh that arrives
 * without those groups falls back to guessing front faces from face direction, as it always did.
 */
const dressSurroundMesh = (mesh: Mesh, materials: Array<MeshStandardMaterial>): void => {
	if (applySurroundMaterials(mesh, materials)) return;

	const texturedMat = materials[materials.length - 1];
	disposeUnusedSurroundMaterials(materials);
	if (!classifySurroundFaces(mesh, texturedMat)) {
		mesh.material = texturedMat;
	}
};

/**
 * Generate cutouts for all islands (matching legacy behavior)
 * This unions all meshes of each type and then subtracts cutouts from both
 * surrounds AND countertops.
 * CSG module is lazy-loaded on first use.
 */
export const generateCutoutsCfn = async (
	self: CutoutManager,
	{ islands, onCompletion }: GenerateCutoutsParams
): Promise<void> => {
	// Lazy load CSG module
	const CSG = await loadCSG();

	for (let islandIndex = 0; islandIndex < islands.length; islandIndex++) {
		const island = islands[islandIndex];
		// Match cutouts by stable island identity, not array position — scene/array
		// order drifts after per-island rebuilds re-append groups, so a positional
		// lookup would subtract another island's holes into this one.
		const islandKey = island.userData?.id as string | undefined;
		const cutouts = islandKey
			? self.getCutoutsForIslandId(islandKey)
			: self.getCutoutsForIsland(islandIndex);
		const hasCutouts = cutouts.length > 0;

		// IMPORTANT:
		// We always union/replace structural meshes (panel/countertop/corner) into stable IDs
		// so that the scene structure does not flip between "merged" and "raw" depending on
		// whether an island currently has cutouts. This stability is required for incremental
		// scene diffing/animations (otherwise the engine sees a structural change and rebuilds).

		// Collect and union meshes by type (matching legacy behavior)
		let surroundsMesh: Mesh | null = null;
		let countertopsMesh: Mesh | null = null;
		let trimsMesh: Mesh | null = null;
		let surroundMaterials: Array<MeshStandardMaterial> | null = null;
		let trimMaterial: MeshStandardMaterial | null = null;
		let countertopDomainId: string | null = null;
		let countertopId = '';
		let surroundId = '';
		let trimId = '';

		// Panels created via CSG intersection (e.g. slat textures) are already
		// extremely high-poly. Running CSG union on them causes OOM.
		// Collect them separately — cutouts are applied per-panel (no union).
		const csgModelPanels: Mesh[] = [];

		// Traverse island to collect meshes by type
		island.traverse((child: unknown) => {
			const meshChild = child as Mesh & { userData?: { type?: string; id?: string; csgModel?: boolean } };
			if (meshChild.type === 'Mesh' && meshChild.userData?.type) {
				meshChild.updateMatrix();
				const { type, csgModel } = meshChild.userData;

				if (type === 'countertop') {
					if (countertopsMesh === null) {
						countertopsMesh = meshChild.clone() as Mesh;
						countertopId = meshChild.userData.id ?? '';
						countertopDomainId = (meshChild.userData as { countertopId?: string }).countertopId ?? null;
					} else {
						countertopsMesh.updateMatrix();
						meshChild.updateMatrix();
						countertopsMesh = CSG.union(countertopsMesh, meshChild) as Mesh;
					}
				} else if (type === 'panel') {
					// Skip CSG-model panels (slat) — their geometry is too complex for CSG ops
					if (csgModel) {
						csgModelPanels.push(meshChild);
						return;
					}
					if (surroundsMesh === null) {
						surroundsMesh = meshChild.clone() as Mesh;
						surroundMaterials = cloneMaterialArray(meshChild.material);
						surroundId = meshChild.userData.id ?? '';
					} else {
						surroundsMesh.updateMatrix();
						meshChild.updateMatrix();
						surroundsMesh = CSG.union(surroundsMesh, meshChild) as Mesh;
					}
				} else if (type === 'corner') {
					if (trimsMesh === null) {
						trimsMesh = meshChild.clone() as Mesh;
						trimMaterial = cloneMaterial(meshChild.material);
						trimId = meshChild.userData.id ?? '';
					} else {
						trimsMesh.updateMatrix();
						meshChild.updateMatrix();
						trimsMesh = CSG.union(trimsMesh, meshChild) as Mesh;
					}
				}
			}
		});

		// Log countertop bounds for debugging
		if (countertopsMesh) {
			(countertopsMesh as Mesh).geometry.computeBoundingBox();
		}

		// Before CSG subtraction, clone the unioned meshes as pre-cutout versions.
		// These hidden meshes are used for animated cross-fade when toggling cutout visibility.
		let preCutoutCountertop: Mesh | null = null;
		let preCutoutPanel: Mesh | null = null;

		if (hasCutouts) {
			if (countertopsMesh) {
				preCutoutCountertop = cloneForCutoutToggle(
					countertopsMesh as Mesh,
					'countertop-no-cutout',
					countertopId ? `${countertopId}-no-cutout` : `island-${islandIndex}-countertop-no-cutout`
				);
			}
			if (surroundsMesh) {
				preCutoutPanel = cloneForCutoutToggle(
					surroundsMesh as Mesh,
					'panel-no-cutout',
					surroundId ? `${surroundId}-no-cutout` : `island-${islandIndex}-panel-no-cutout`
				);
			}
		}

		// Apply cutouts to both surrounds and countertops (only when needed)
		for (const cutout of cutouts) {
			if (!hasCutouts) break;

			const holeMesh = createCutoutMeshCfn({ cutout });

			if (surroundsMesh) {
				(surroundsMesh as Mesh).updateMatrix();
				addColorAttribute(surroundsMesh);
			}
			if (countertopsMesh) {
				(countertopsMesh as Mesh).updateMatrix();
				addColorAttribute(countertopsMesh);
			}
			addColorAttribute(holeMesh);

			// Subtract hole from surrounds
			if (surroundsMesh) {
				try {
					surroundsMesh = CSG.subtract(surroundsMesh, holeMesh) as Mesh;
				} catch (e) {
					console.error('CutoutManager: CSG subtract failed for surround:', e);
				}
			}

			// Subtract hole from countertops. A surround-only opening (a vent) is skipped: it sits
			// hard against the underside of the slab, so cutting the counter with it would punch
			// the worktop as well as the panel.
			if (countertopsMesh && !cutout.surroundOnly) {
				try {
					(countertopsMesh as Mesh).geometry.computeBoundingBox();
					countertopsMesh = CSG.subtract(countertopsMesh, holeMesh) as Mesh;
				} catch (e) {
					console.error('CutoutManager: CSG subtract failed for countertop:', e);
				}
			}

			// Clean up hole mesh
			holeMesh.geometry.dispose();
			(holeMesh.material as MeshStandardMaterial).dispose();
		}

		if (countertopsMesh) {
			fixCounterMaterials(countertopsMesh);
		}

		// Apply cutouts to individual CSG-model (slat) panels without unioning them.
		// Each panel is processed separately to avoid the OOM caused by union operations.
		const preCutoutSlatClones: Array<Mesh> = [];

		if (hasCutouts && csgModelPanels.length > 0) {
			const enableShadowsForSlats = self.config.qualitySettings?.shadowMapEnabled ?? true;

			for (let s = 0; s < csgModelPanels.length; s++) {
				const slatPanel = csgModelPanels[s];

				// Clone before any modification for toggle animation
				const preCutoutClone = cloneForCutoutToggle(
					slatPanel,
					'panel-no-cutout',
					`${slatPanel.userData?.id ?? `slat-${s}`}-no-cutout`
				);

				let resultMesh: Mesh = slatPanel;
				let anyApplied = false;

				// Compute panel bounding box once for intersection checks
				slatPanel.geometry.computeBoundingBox();
				slatPanel.updateMatrixWorld(true);
				const panelBox = new Box3().copy(slatPanel.geometry.boundingBox!).applyMatrix4(slatPanel.matrixWorld);

				for (const cutout of cutouts) {
					const holeMesh = createCutoutMeshCfn({ cutout });

					// Cheap bounding box pre-check — skip CSG if no overlap
					holeMesh.geometry.computeBoundingBox();
					holeMesh.updateMatrixWorld(true);
					const holeBox = new Box3().copy(holeMesh.geometry.boundingBox!).applyMatrix4(holeMesh.matrixWorld);

					if (!panelBox.intersectsBox(holeBox)) {
						holeMesh.geometry.dispose();
						(holeMesh.material as MeshStandardMaterial).dispose();
						continue;
					}

					resultMesh.updateMatrix();
					addColorAttribute(resultMesh);
					addColorAttribute(holeMesh);

					try {
						const newResult = CSG.subtract(resultMesh, holeMesh) as Mesh;
						// Dispose intermediate geometry if we already replaced the original
						if (resultMesh !== slatPanel) {
							resultMesh.geometry.dispose();
						}
						resultMesh = newResult;
						anyApplied = true;
					} catch (e) {
						console.error('CutoutManager: CSG subtract failed for slat panel:', slatPanel.userData?.id, e);
					}

					holeMesh.geometry.dispose();
					(holeMesh.material as MeshStandardMaterial).dispose();
				}

				if (anyApplied) {
					// Preserve userData with csgModel flag
					resultMesh.userData = { ...slatPanel.userData, csgModel: true };

					// Keep existing material (flatShading, single mat) — no face classification needed
					resetMaterialOpacity(resultMesh.material as MeshStandardMaterial);

					resultMesh.castShadow = enableShadowsForSlats;
					resultMesh.receiveShadow = enableShadowsForSlats;

					csgModelPanels[s] = resultMesh;
				}

				// Always create a pre-cutout clone for every slat panel so the toggle
				// animation has a replacement to fade in. For panels without cutouts the
				// clone is visually identical — fading one out and the other in is a no-op.
				preCutoutSlatClones.push(preCutoutClone);
			}
		}

		// Replace island contents with cutout meshes (or normalized structural meshes if no cutouts)
		const hasCsgModelPanels = csgModelPanels.length > 0;
		if (surroundsMesh || countertopsMesh || trimsMesh || hasCsgModelPanels) {
			// Build a set of CSG-model panel references so we can skip disposing them
			const csgModelPanelSet = new Set<unknown>(csgModelPanels);

			// Remove structural children and cutout-related meshes (leave appliances/other items intact)
			const structuralTypes = new Set(['panel', 'countertop', 'corner', 'appliance-cutout', 'countertop-no-cutout', 'panel-no-cutout']);
			const childrenCopy = [...island.children];
			for (const child of childrenCopy) {
				const childMesh = child as Mesh & { userData?: { type?: string }; geometry?: { dispose: () => void }; material?: unknown };
				const childType = childMesh.userData?.type;
				if (!structuralTypes.has(childType as string)) {
					continue;
				}

				// Preserve CSG-model panels that are still in the set.
				// Modified panels have been replaced in csgModelPanels, so originals
				// won't match and will be disposed; unmodified ones stay in place.
				if (csgModelPanelSet.has(child)) {
					continue;
				}

				if (childMesh.geometry) {
					childMesh.geometry.dispose();
				}
				if (childMesh.material) {
					const mat = childMesh.material;
					if (Array.isArray(mat)) {
						mat.forEach((m: { dispose?: () => void }) => m.dispose?.());
					} else {
						(mat as { dispose?: () => void }).dispose?.();
					}
				}
				island.remove(child);
			}

			// Add result meshes back
			const enableShadows = self.config.qualitySettings?.shadowMapEnabled ?? true;

			if (surroundsMesh && surroundMaterials) {
				surroundMaterials.forEach(resetMaterialOpacity);
				dressSurroundMesh(surroundsMesh as Mesh, surroundMaterials);
				(surroundsMesh as Mesh).userData = { type: 'panel', id: surroundId || `island-${islandIndex}-surround` };
				(surroundsMesh as Mesh).castShadow = enableShadows;
				(surroundsMesh as Mesh).receiveShadow = enableShadows;
				island.add(surroundsMesh);
			}

			if (countertopsMesh) {
				const cMat = (countertopsMesh as Mesh).material;
				if (Array.isArray(cMat)) {
					cMat.forEach((m: MeshStandardMaterial) => resetMaterialOpacity(m));
				} else {
					resetMaterialOpacity(cMat as MeshStandardMaterial);
				}
				(countertopsMesh as Mesh).userData = { type: 'countertop', id: countertopId || `island-${islandIndex}-countertop`, countertopId: countertopDomainId };
				(countertopsMesh as Mesh).castShadow = enableShadows;
				(countertopsMesh as Mesh).receiveShadow = enableShadows;
				island.add(countertopsMesh);
			}

			// Add pre-cutout clones (hidden) for cross-fade cutout toggling
			if (preCutoutCountertop) {
				preCutoutCountertop.castShadow = enableShadows;
				preCutoutCountertop.receiveShadow = enableShadows;
				island.add(preCutoutCountertop);
			}
			if (preCutoutPanel && surroundMaterials) {
				dressSurroundMesh(preCutoutPanel, surroundMaterials.map((mat) => mat.clone() as MeshStandardMaterial));
				preCutoutPanel.castShadow = enableShadows;
				preCutoutPanel.receiveShadow = enableShadows;
				island.add(preCutoutPanel);
			}

			if (trimsMesh && trimMaterial) {
				resetMaterialOpacity(trimMaterial);
				(trimsMesh as Mesh).material = trimMaterial;
				(trimsMesh as Mesh).userData = { type: 'corner', id: trimId || `island-${islandIndex}-trim` };
				(trimsMesh as Mesh).castShadow = enableShadows;
				(trimsMesh as Mesh).receiveShadow = enableShadows;
				island.add(trimsMesh);
			}

			// Re-add CSG-model (slat) panels — possibly with cutouts applied
			for (const slatPanel of csgModelPanels) {
				slatPanel.castShadow = enableShadows;
				slatPanel.receiveShadow = enableShadows;
				island.add(slatPanel);
			}

			// Add pre-cutout slat clones (hidden) for cross-fade cutout toggling
			for (const slatClone of preCutoutSlatClones) {
				slatClone.castShadow = enableShadows;
				slatClone.receiveShadow = enableShadows;
				island.add(slatClone);
			}
		}
	}

	// Clear processed cutouts
	self.clearCutouts();

	onCompletion?.();
};
